import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Wand2, Film, Mic, Music2, Plus, Crown, Sparkles, Layers } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const counts = {
    flyer: projects.filter(p=>p.kind==="flyer").length,
    video: projects.filter(p=>p.kind==="video").length,
    voice: projects.filter(p=>p.kind==="voice").length,
    music: projects.filter(p=>p.kind==="music").length,
    ad: projects.filter(p=>p.kind==="ad").length,
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <span className="label-mono">Studio personnel</span>
          <h1 className="mt-2 text-4xl font-heading tracking-tighter">
            Bonjour <span className="gold-text">{user?.name?.split(" ")[0] || "créateur"}</span>
          </h1>
          <p className="text-white/55 mt-2">Vos créations, vos brouillons, votre univers publicitaire.</p>
        </div>
        <div className="flex items-center gap-3">
          {user?.promo_used && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 bg-gold/5">
              <Crown className="w-4 h-4 text-gold" />
              <span className="label-mono">Membre VIP OR</span>
            </div>
          )}
          <div className="px-4 py-2 rounded-full border border-white/10">
            <span className="label-mono">{user?.credits ?? 0} crédits</span>
          </div>
        </div>
      </div>

      {/* Bento */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-5 mb-10">
        <StudioTile to="/studio/flyer" icon={Wand2} title="Flyer IA" count={counts.flyer} hint="Gemini Nano Banana" big />
        <StudioTile to="/studio/video" icon={Film} title="Vidéo Pub" count={counts.video} hint="Sora 2" />
        <StudioTile to="/studio/voice" icon={Mic} title="Voix Off" count={counts.voice} hint="ElevenLabs" />
        <StudioTile to="/studio/music" icon={Music2} title="Musique IA" count={counts.music} hint="Suno V4.5" />
        <StudioTile to="/studio/composer" icon={Layers} title="Pub finale" count={counts.ad} hint="Assembleur" />
      </div>

      {/* Recent */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-heading text-2xl tracking-tight">Vos créations</h2>
        <Link data-testid="dashboard-pricing-link" to="/pricing" className="text-sm text-gold/80 hover:text-gold">Tarifs & packs →</Link>
      </div>
      {loading ? (
        <div className="grid md:grid-cols-3 gap-4">
          {[1,2,3].map(i=> <div key={i} className="aspect-video rounded-xl shimmer-bg" />)}
        </div>
      ) : projects.length === 0 ? (
        <div className="gold-border-gradient p-10 text-center">
          <Sparkles className="w-8 h-8 text-gold mx-auto mb-3" />
          <h3 className="font-heading text-2xl">Aucune création pour l&apos;instant</h3>
          <p className="text-white/55 mt-2">Lancez votre premier projet en quelques secondes.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/studio/flyer" data-testid="empty-cta-flyer" className="gold-btn"><Wand2 className="w-4 h-4" /> Créer un flyer</Link>
            <Link to="/studio/video" data-testid="empty-cta-video" className="ghost-btn"><Film className="w-4 h-4" /> Générer une vidéo</Link>
            <Link to="/studio/voice" data-testid="empty-cta-voice" className="ghost-btn"><Mic className="w-4 h-4" /> Voix off</Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {projects.map(p => (
            <ProjectCard key={p.project_id} p={p} />
          ))}
        </div>
      )}
    </div>
  );
}

const StudioTile = ({ to, icon: Icon, title, count, hint, big }) => (
  <Link to={to} data-testid={`tile-${title.toLowerCase().replace(/\s/g,'-')}`} className={`studio-card p-6 group flex flex-col justify-between ${big ? "md:row-span-2 md:min-h-[260px]" : ""}`}>
    <div className="flex items-start justify-between">
      <div className="w-12 h-12 rounded-xl bg-gold/10 text-gold border border-gold/30 flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <Plus className="w-4 h-4 text-white/40 group-hover:text-gold" />
    </div>
    <div className="mt-6">
      <div className="text-3xl font-heading tracking-tighter">{title}</div>
      <div className="text-xs text-white/40 mt-1">{hint}</div>
      <div className="mt-3 text-xs text-gold/80">{count} création{count!==1?"s":""}</div>
    </div>
  </Link>
);

const ProjectCard = ({ p }) => (
  <Link to={`/studio/${p.kind}`} data-testid={`project-${p.project_id}`} className="studio-card overflow-hidden">
    <div className="aspect-video bg-obsidian relative overflow-hidden">
      {p.thumbnail_url && p.kind === "flyer" ? (
        <img src={p.thumbnail_url} alt={p.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-white/30">
          {p.kind === "video" ? <Film className="w-8 h-8" /> : p.kind === "voice" ? <Mic className="w-8 h-8" /> : p.kind === "music" ? <Music2 className="w-8 h-8" /> : <Wand2 className="w-8 h-8" />}
        </div>
      )}
      <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] uppercase tracking-widest text-gold border border-gold/30">{p.kind}</div>
    </div>
    <div className="p-3">
      <div className="text-sm truncate">{p.title}</div>
      <div className="text-[10px] text-white/40 mt-1 uppercase tracking-widest">{new Date(p.created_at).toLocaleDateString("fr-FR")}</div>
    </div>
  </Link>
);
